// ── Labor & Equipment: per diem on a 0-hour HeavyJob line ──
//
// HeavyJob carries a crew's per diem on every line of the card, worked or not,
// and its API gives the adder no count (quantity is 0 on every line). So the
// HeavyJob read keeps a per diem only for someone with hours on the card: the
// tickets and billing never pick one up off a 0-hour line. The read lists those
// lines on their own (readHeavyJobCards `idlePerDiem`), and payroll does pay
// some of them: weekend per diem, mostly (Seth 11/4: "why is heavy job per diem
// should be one on the yellow lines" — 71's 10/17, seven people with 0 hours, a
// per diem line on the card, and JDE per diem).
//
// withIdlePerDiem puts those lines into the grid's Heavy Job Per Diem column
// only:
//   - a person-day the grid already shows (a "JDE, no ticket" row, mostly)
//     with no HeavyJob per diem on any of its rows takes 1, on the row that
//     holds the JDE per diem;
//   - a person-day with no row, on a date JDE has posted, comes in as a row of
//     its own with HeavyJob 1 and JDE 0: "Per diem differs".
// Nothing here reaches a ticket, a Record or an invoice.

import { HJ_STATUS, JDE_STATUS, RECON_TOL, normId, rowOrder } from './billingRecon';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const pk = (d, id) => `${d}|${normId(id)}`;

/**
 * rows      the grid's rows (withJdeOnly's output)
 * idle      readHeavyJobCards' idlePerDiem: [{ job, date, id, name }]
 * jdeDates  the work dates JDE has labor or per diem lines for
 * names     { [normId]: name } off the rest of the span, for a row of its own
 *           (the SQL copy's adjustment list carries no name)
 */
export const withIdlePerDiem = (rows, idle, { jdeDates = new Set(), names = {} } = {}) => {
    const list = rows || [];
    if (!(idle || []).length) return list;
    const want = new Map();
    (idle || []).forEach((x) => {
        if (!x?.date || !x?.id) return;
        const k = pk(String(x.date).slice(0, 10), x.id);
        if (!want.has(k)) want.set(k, { d: String(x.date).slice(0, 10), id: String(x.id).trim(), name: x.name || '' });
    });
    if (!want.size) return list;

    const byPd = {};
    list.forEach((r, i) => {
        if (r.k !== 'Labor') return;
        (byPd[pk(r.d, r.emp)] = byPd[pk(r.d, r.emp)] || []).push(i);
    });
    const out = [...list];
    let added = false;
    want.forEach((x, k) => {
        const at = byPd[k];
        if (at) {
            if (at.some((i) => num(out[i].hjPd) > 0)) return;
            const i = at.find((j) => out[j].jdePd != null) ?? at[0];
            const r = out[i];
            // Hours already agree on a Match / Per diem differs row, so the
            // per diem alone decides between the two; any other status stands.
            let { jdeStatus } = r;
            if ((jdeStatus === JDE_STATUS.match || jdeStatus === JDE_STATUS.perDiem) && r.jdePd != null) {
                jdeStatus = Math.abs(num(r.jdePd) - 1) > RECON_TOL ? JDE_STATUS.perDiem : JDE_STATUS.match;
            }
            out[i] = { ...r, hjPd: 1, hjPdIdle: true, jdeStatus };
            return;
        }
        if (!jdeDates.has(x.d)) return;
        added = true;
        out.push({
            id: null, d: x.d, sh: '', k: 'Labor', cc: '', ccd: '',
            emp: x.id, name: x.name || names[normId(x.id)] || '', unit: '', unitDesc: '', title: '', cls: '', card: '',
            hj: 0, hjSt: 0, hjOt: 0, hjPd: 1, hjPdIdle: true, ft: 0,
            hjNow: 0, hjNowSt: 0, hjNowOt: 0, delta: 0, status: HJ_STATUS.same, ticketGap: false,
            jdeSt: null, jdeOt: null, jde: null, jdePd: 0, jdeKeys: [], jdeLines: [], jdePdLines: [],
            billedNow: '', invs: [], jdeCc: '', jdeStatus: JDE_STATUS.perDiem, jdeDelta: null, idleOnly: true,
        });
    });
    return added ? out.sort(rowOrder) : out;
};

/** The work dates JDE has posted labor or per diem for (billing txns). */
export const jdeLaborDates = (txns) => new Set((txns || [])
    .filter((t) => t && t.date && ['51100', '51200', '51500'].includes(t.ct))
    .map((t) => String(t.date).slice(0, 10)));

/** { [normId]: name } off HeavyJob rows and grid rows, for idle rows the SQL copy left unnamed. */
export const namesById = (hjRows, rows) => {
    const m = {};
    (rows || []).forEach((r) => { if (r.k === 'Labor' && r.emp && r.name) m[normId(r.emp)] = r.name; });
    (hjRows || []).forEach((r) => { if (r.Staff_ID && r.Staff_Name && !m[normId(r.Staff_ID)]) m[normId(r.Staff_ID)] = String(r.Staff_Name).trim(); });
    return m;
};
